import _get from 'lodash/get'
import { z } from 'zod'

import type { AnyStepContentElement } from '@flows/core/types/stepContentTypes'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'
import type { ProductKey } from '@flows/insurance/types/products'
import { getProductHelperPath } from '@flows/insurance/utils'

export const getQuotedZipCodePath = (productKey: ProductKey) =>
  getProductHelperPath(productKey, 'quoted_zip_code')

const isZipCodeChanged = (values: unknown, quotedZipCodePath: string) => {
  const quotedZipCode = _get(values, quotedZipCodePath)
  const zipCode = _get(values, 'zip_code')
  return Boolean(quotedZipCode) && Boolean(zipCode) && zipCode !== quotedZipCode
}

const holdsAddressField = (element: unknown): boolean =>
  Array.isArray(element)
    ? element.some(holdsAddressField)
    : _get(element, 'type') === 'addressAutocomplete'

export const withQuotedZipCodeCheck = <TStep extends AnyStepConfiguration>(
  step: TStep & { schema: z.AnyZodObject },
  { productKey, translationKey }: { productKey: ProductKey; translationKey: string }
) => {
  const quotedZipCodePath = getQuotedZipCodePath(productKey)

  const notice = {
    type: 'infoBox',
    showInfoIcon: true,
    iconName: 'Warning',
    iconColor: 'error',
    bgColor: 'errorLight',
    contentConfiguration: [{ type: 'text', text: { type: 'translatableText', translationKey } }],
    condition: {
      type: 'function',
      valuePaths: ['zip_code', quotedZipCodePath],
      function: (values: unknown) => isZipCodeChanged(values, quotedZipCodePath),
    },
  } as const satisfies AnyStepContentElement

  return {
    ...step,
    stepContent: step.stepContent?.flatMap((element) =>
      holdsAddressField(element) ? [element, notice] : [element]
    ),
    schema: z
      .unknown()
      .superRefine((values, ctx) => {
        if (isZipCodeChanged(values, quotedZipCodePath)) {
          ctx.addIssue({
            path: ['zip_code_matches_quote'],
            code: z.ZodIssueCode.custom,
            message: translationKey,
          })
        }
      })
      .pipe(step.schema),
    onSubmit: {
      type: 'simpleHook' as const,
      callback: (flowValues: FlowValues) =>
        isZipCodeChanged(flowValues, quotedZipCodePath)
          ? { error: { type: 'translatableText', translationKey } as const }
          : undefined,
    },
  }
}
